/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { HFlow, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { Box } from 'components/Box'
import { SimNaoEnum, SimNaoRadioGroupField } from 'components/form'
import { RequiredLabel } from 'components/form/final-form/RequiredLabel/RequiredLabel'
import { MetaPath, MetaRoot } from 'util/metaPath'

import { PassouFalhouEnum } from '../model-consultaSeguimento'

export type ExemplosPassouFalhouVerticalFieldModel<T extends string> = Record<PassouFalhouEnum, Record<T, SimNaoEnum>>

export type ExemplosPassouFalhouVerticalFieldOptions<T extends string> = Record<
  PassouFalhouEnum,
  Array<{ value: T; label: string }>
>

interface ExemplosPassouFalhouVerticalFieldProps<T extends string> {
  name: MetaPath<ExemplosPassouFalhouVerticalFieldModel<T>>
  label: string
  options: ExemplosPassouFalhouVerticalFieldOptions<T>
  required?: boolean
  disabled?: boolean
}

export function ExemplosPassouFalhouVerticalField<T extends string>(
  props: Readonly<ExemplosPassouFalhouVerticalFieldProps<T>>
) {
  const { name, label, options, required, disabled } = props

  const pathPassou = name.PASSOU as MetaRoot<Record<T, SimNaoEnum>>
  const pathFalhou = name.FALHOU as MetaRoot<Record<T, SimNaoEnum>>

  const theme = useTheme()
  const styles = createStyles(theme)

  return (
    <VFlow vSpacing={0.5}>
      {required ? (
        <RequiredLabel color={disabled ? 'disabled' : 'normal'} fontWeight='bold' label={label} />
      ) : (
        <Text color={disabled ? 'disabled' : 'normal'} fontWeight='bold'>
          {label}
        </Text>
      )}
      <Box style={styles.box}>
        <HFlow style={styles.section}>
          <Text style={styles.sectionTitle}>Exemplos Passou</Text>
          <VFlow vSpacing={0} style={styles.sectionList}>
            {options.PASSOU.map((item) => (
              <HFlow key={item.value} justifyContent='space-between' style={styles.sectionListItem}>
                <Text>{item.label}</Text>
                <SimNaoRadioGroupField name={pathPassou[item.value]} />
              </HFlow>
            ))}
          </VFlow>
        </HFlow>
        <HFlow style={styles.section}>
          <Text style={styles.sectionTitle}>Exemplos Falhou</Text>
          <VFlow vSpacing={0} style={styles.sectionList}>
            {options.FALHOU.map((item) => (
              <HFlow key={item.value} justifyContent='space-between' style={styles.sectionListItem}>
                <Text>{item.label}</Text>
                <SimNaoRadioGroupField name={pathFalhou[item.value]} />
              </HFlow>
            ))}
          </VFlow>
        </HFlow>
      </Box>
    </VFlow>
  )
}

const createStyles = (theme: Theme) => ({
  box: css`
    padding: 0;
  `,
  section: css`
    grid-gap: 0;
    grid-template-columns: 5.75rem 1fr;
    &:not(:last-child) {
      border-bottom: 1px solid ${theme.pallete.gray.c60};
    }
  `,
  sectionTitle: css`
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 1rem;
    background: ${theme.pallete.gray.c90};
  `,
  sectionList: css`
    & > div:not(:last-child) {
      border-bottom: 1px solid ${theme.pallete.divider};
    }
  `,
  sectionListItem: css`
    padding: 0.75rem 1rem;
  `,
})
